import React, { useEffect, useId, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { WhatsAppIcon } from './WhatsAppIcon';
import { getE2DEmail, buildE2DWhatsAppUrl } from './e2dContact';
import '../../css/visualComponent/E2DAttribution.css';

const EmailIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2.5" />
    <path d="m4 7 8 6 8-6" />
  </svg>
);

export const E2DAttribution = ({ logoSrc }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const closeTimer = useRef(null);
  const suppressOpen = useRef(false);
  const popoverId = useId();

  const email = getE2DEmail();
  const whatsappUrl = buildE2DWhatsAppUrl();

  const cancelClose = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => {
    if (!open) return;
    const onPointer = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        suppressOpen.current = true;
        flushSync(() => setOpen(false));
        buttonRef.current?.focus();
        suppressOpen.current = false;
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  useEffect(() => () => cancelClose(), []);

  const onBlur = (e) => {
    if (containerRef.current && containerRef.current.contains(e.relatedTarget)) return;
    scheduleClose();
  };

  return (
    <div
      ref={containerRef}
      className="e2d-root"
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
      onFocus={() => { if (suppressOpen.current) return; cancelClose(); setOpen(true); }}
      onBlur={onBlur}
    >
      <button
        ref={buttonRef}
        type="button"
        className="e2d-pill"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={() => setOpen((p) => !p)}
      >
        <span className="e2d-logo">
          <img src={logoSrc} alt="" width={24} height={24} />
        </span>
        <span className="e2d-text">
          <span className="e2d-text-top">Designed by</span>
          <span className="e2d-text-main">Evolve2Digital</span>
        </span>
      </button>

      {open && (
        <div id={popoverId} role="dialog" aria-label="Evolve2Digital contact" className="e2d-popover">
          <p className="e2d-popover-title">Let's talk</p>
          <ul className="e2d-list">
            <li>
              <a className="e2d-link" href={`mailto:${email}`}>
                <span className="e2d-link-icon"><EmailIcon className="e2d-icon" /></span>
                <span className="e2d-link-text">
                  <span className="e2d-link-label">Email</span>
                  <span className="e2d-link-value">{email}</span>
                </span>
              </a>
            </li>
            <li>
              <a className="e2d-link" href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                <span className="e2d-link-icon"><WhatsAppIcon className="e2d-icon" /></span>
                <span className="e2d-link-text">
                  <span className="e2d-link-label">WhatsApp</span>
                  <span className="e2d-link-value">Message us</span>
                </span>
              </a>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};
